import type { TFunction } from 'i18next'

export interface ServerError {
  code?: string
  message?: string
  details?: Record<string, unknown>
}

const CODE_TO_I18N: Record<string, string> = {
  'equipment.duplicateTag.inPlant': 'errors.uniqueness.equipmentDuplicateTagInPlant',
  'location.duplicateCode.inPlant': 'errors.uniqueness.locationDuplicateCodeInPlant',
  'team.duplicateName.inPlant': 'errors.uniqueness.teamDuplicateNameInPlant',
  'technician.duplicatePersonnelNumber.inTenant':
    'errors.uniqueness.technicianDuplicatePersonnelNumberInTenant',
  // Achado do revisor adversário no PR #633: o motor's `requireAuth`
  // (`shared/middleware/clerk-auth.ts` — HTTP_FOR_REASON) devolve estes
  // 4 códigos de OPERAÇÃO como string no MESMO envelope `{code, message}`
  // — e o generic fallback (removido acima) fazia isso funcionar por
  // acidente, mostrando o `message` cru. Mapeados explicitamente para não
  // regredir esse caminho real quando o fallback de texto cru saiu.
  // `USER_NOT_APPROVED` nunca chega aqui via `chat-stream.ts` hoje (a
  // branch `response.status === 403` ali mostra `errors.permissionDenied`
  // ANTES de tentar traduzir o corpo) — mapeado mesmo assim porque é a
  // função pura que faz a tradução, e essa blindagem de status pode mudar
  // sem que ninguém lembre de revisitar este mapa.
  INVALID_TENANT: 'errors.auth.invalidTenant',
  CLERK_UNAVAILABLE: 'errors.auth.providerUnavailable',
  CLERK_NOT_CONFIGURED: 'errors.auth.notConfigured',
  USER_NOT_APPROVED: 'errors.auth.notApproved',
  // STO-1300 / STO-223: o motor passou a mandar `code` estruturado no evento
  // SSE `error` (antes só o genérico chegava aqui). Mapeados para a frase
  // honesta do motor em vez do fallback genérico.
  LLM_TEMPORARILY_UNAVAILABLE: 'errors.stream.llmUnavailable',
  TURN_DEADLINE_EXCEEDED: 'errors.stream.turnDeadlineExceeded',
  INTERNAL_ERROR: 'errors.stream.internalError',
  // A replay refused by the idempotency guard is not a generic execution failure.
  in_flight: 'errors.stream.requestUnconfirmed',
}

/**
 * Turn an engine error envelope `{code, message, details}` into a localized
 * string. Prefers the mapped i18n key + interpolated `details`; any code NOT
 * in the map (including the engine's numeric HTTP-status `code` on generic
 * failures) falls back to a generic translation.
 *
 * Deliberately never returns `err.message` verbatim: the engine's `message`
 * on an unmapped error is, for the one caller that matters (an uncaught
 * exception re-thrown by `chat-rest.ts`/`chat-stream.ts` as `{code:
 * <httpStatus>, message: error.message}`), a raw JS `Error.message` — often
 * English, always unfit for the chat bubble. Callers that want the raw text
 * for a support log read it themselves BEFORE calling this function; this
 * function only ever returns UI-safe, translated copy.
 *
 * KNOWN LIMITATION (achado do revisor, PR #633): the engine's
 * `InvalidTenantIdError` catch in `chat-rest.ts` (linhas ~276-283, rota
 * `/api/v1/chat/message`) sends the EXACT SAME "Tenant inválido ou ausente"
 * text as `requireAuth`'s `INVALID_TENANT` case above — but as `{code: 400,
 * message: '...'}`, a NUMERIC code with no string discriminator. This
 * function cannot tell that 400 apart from any other unhandled 400 (there is
 * no field to match on without brittle text-matching against `message`, which
 * breaks the moment the wording or the active locale changes), so it falls
 * back to the generic message for that specific case — a deliberate
 * regression-in-trade against showing raw/wrong text for an UNRELATED 400.
 * Fixing it needs the engine to send a string `code` there too (out of this
 * frontend repo's scope). Tracked as follow-up, not fixed here.
 */
export function translateServerError(err: ServerError | null | undefined, t: TFunction): string {
  if (err?.code) {
    const key = CODE_TO_I18N[err.code]
    if (key) return t(key, (err.details ?? {}) as Record<string, unknown>)
  }
  return t('errors.generic')
}
